import { ZenputCodes } from '../../constants';
import { isWindows } from '../../shared/utils/is';
import { MobileAccounts, MobileBaseSearchOptions, ProjectTaskSearchOptions } from '../models';
import {
    BaseDashboardCardResultList,
    BaseDashboardCardResultListItem,
    BaseZenputCollection,
    BaseZenputModel,
    DashboardCardResultListWithHeadersMixin,
    DistributeGroup,
    DistributeGroups,
    Paginator,
    SearchCriteria,
    SearchCriterias,
    SearchOption,
    TopItemHeaders
} from '../shared/forms';

/**
 * Search options for mobile dashboard.
 *
 * For now, this just holds a distribute group (team), so it's best to generate it with
 * $f.mobile.dashboard.get_active_account_or_group().get_dashboard_search_options()
 *
 * @see MobileDistributeGroup.get_dashboard_search_options()
 */
export const DashboardSearchOptions = MobileBaseSearchOptions.extend({
    cache_enabled: false, // unlike our other search options, we will not persist this to disk. Instead,
    // we'll keep this object alive as long as we need it

    /**
     * Because this will not be used as a global constant and will always be set by the creator, we don't
     * need to define our initial options ourselves.
     */
    initialize_options: function () {}
});

export const MobileDistributeGroup = DistributeGroup.extend({
    /**
     * Get a dashboard search options object to be used for querying from the server.
     *
     * @return {DashboardSearchOptions} The generated dashboard search options
     */
    get_dashboard_search_options: function () {
        return new DashboardSearchOptions([
            {
                field: 'group_id',
                display_value: this.get('name'),
                value: this.id
            }
        ]);
    }
});

export const MobileDistributeGroups = DistributeGroups.extend({
    // Important Note: With the cache enabled, the get_hierarchy post-processing we d woill be lost without a listener
    // for the sync of this method re-calling that work
    cache_enabled: true,
    cache_key: 'company_distribute_groups',

    hierarchy_connect_true_root: true,
    _active_hierarchy_group: null, // keep track of where we are in the hierarchy

    initialize: function (options) {
        MobileDistributeGroups.__super__.initialize.apply(this, arguments);

        // @author:david our backbone cache will not work if we have a fetch_limit that's lower than our paginator limit.
        // Our paginator is set to 9999 in our base collection, so we need to be sure that our fetch limit as high as
        // whatever is set there. @jira:MOBILE-622
        this.cache_fetch_limit = this.paginator.get('limit');
    },

    /**
     * We are defining this as a function due to load-time dependency issues with DistributeGroup.submodels
     */
    model: function (attrs, options) {
        return new MobileDistributeGroup(attrs, options);
    },

    /**
     * Get the root group for this company
     * @returns {DistributeGroup}
     */
    get_root_group: function () {
        return this.get_hierarchy(true, true).findWhere({ is_root: true });
    }
});

/**
 * Collection of accounts under a certain distribute_group
 */
export const AccountsForDistributeGroup = MobileAccounts.extend({
    cache_key: null, // set in initialize
    distribute_group_id: null,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.distribute_group_id) {
            throw new Error('Programmer Error: distribute_group_id missing from AccountsForDistributeGroup');
        }

        AccountsForDistributeGroup.__super__.initialize.apply(this, arguments);

        this.paginator.set({
            group_id: this.distribute_group_id,
            include_group_children: false,
            order_by: 'name'
        });
    },

    get_cache_key: function (options) {
        return 'accounts_for_group_' + this.distribute_group_id;
    }
});

/**
 * A search criteria list that holds the common dashboard filters, which for now are just the date range.
 * We will set these filters by default, and we will not persist them to disk, though we may store more in
 * here someday.
 */
export const DashboardFilterList = SearchCriterias.extend({
    initialize: function (options) {
        DashboardFilterList.__super__.initialize.apply(this, arguments);

        if (this.length) {
            return;
        }

        this.reset_from_another({
            search: this.get_default_search_criterias()
        });
    },

    get_default_search_criterias: function () {
        return [
            {
                field_id: 'dashboard_datetime',
                operator_id: 'date_occur_after',
                value: [4, 'weeks']
            }
        ];
    },

    /**
     * Unlike our other search criterias, the filters should only be the list.
     */
    toJSON: function () {
        return this.models;
    }
});

/* eslint-disable-next-line max-len */
export const DashboardFilterListForLogbook = DashboardFilterList.extend(
    {
        frequency: null, // this will change the number of weeks/months/days we show
        paginator: null, // Paginator - we will translate start/limit into search for horizontal scroll pagination
        // default_paginator_limit: null, // defined as static variable below

        initialize: function (options) {
            options = options || {};
            var this_ = this;

            if (!this.paginator && !options.paginator) {
                this.paginator = new Paginator({
                    start: 0,
                    limit: this.default_paginator_limit
                });
            }

            DashboardFilterListForLogbook.__super__.initialize.apply(this, arguments);

            // When our paginator start/limit changes, update our search criteria
            this.listenTo(this.paginator, 'change', function () {
                var search = this_.findWhere({ field_id: 'dashboard_datetime' });

                if (!search) {
                    throw new Error('Unable to find dashboard_datetime search criteria when scroll-paginating.');
                }

                search.set('value', this._get_date_occur_range_value(search.get('value')[2]));
            });
        },

        get_default_search_criterias: function () {
            var bucket = null;
            if (this.frequency === $f.utils.recurring.frequencies.DAILY) {
                bucket = 'days';
            } else if (this.frequency === $f.utils.recurring.frequencies.WEEKLY) {
                bucket = 'weeks';
            } else {
                bucket = 'months';
            }

            return [
                {
                    field_id: 'dashboard_datetime',
                    operator_id: 'date_occur_range',
                    value: this._get_date_occur_range_value(bucket)
                }
            ];
        },

        /**
         * The date_occur_range doesn't use start,limit but instead uses start,end. We use a paginator to make the front-
         * end as similar to other pagination as possible, but we need to convert to this format every time the paginator
         * changes.
         */
        _get_date_occur_range_value: function (bucket_type) {
            // @author:bosco Pagination currently only handles in the past.  When we do future limit should be - for past
            // and positive for future

            var pag_start = this.paginator.get('start');
            var pag_limit = this.paginator.get('limit');
            var occur_start = null;
            var occur_end = 0;

            if (pag_start !== 0) {
                occur_start = -pag_start - pag_limit;
                occur_end = -pag_start;
            } else {
                occur_start = pag_start - pag_limit;
            }

            return [occur_start, occur_end, bucket_type];
        }
    },
    {
        default_paginator_limit: 20
    }
);

/**
 * Config model for Dashboard Cards and Card Components, lets us instantiate models for our listViews
 *
 * Only holds the view class and any options we want to pass down into the view class
 */
export const DashboardViewConfiguration = BaseZenputModel.extend({
    defaults: {
        view_options: {},
        view_class: null
    }
});

/**
 *  Hold our DashboardCard and DashboardCardComponent
 */
export const DashboardViewConfigurations = BaseZenputCollection.extend({
    model: DashboardViewConfiguration
});

/**
 * Currently no changed functionality on the mobile version of this.
 */
export const MobileBaseDashboardCardResultList = BaseDashboardCardResultList.extend({
    cache_enabled: true,
    cache_ttl: 120000, // 2 minutes
    url: '/api/v1/dashboards/dashboard/mobile',

    // we are going to set the cache_key at fetch time dynamically
    fetch: function (options) {
        this.cache_key = this.get_cache_key(options);
        return MobileBaseDashboardCardResultList.__super__.fetch.apply(this, arguments);
    },

    /**
     * Set the cache key for this dashboard card
     *
     * Need to make cache_key unique based on search_criteria, paginator and dashboard_filters
     */
    get_cache_key: function (options) {
        // this logic is within the fetch method of the parent class, duplicating here so the cache key has values
        // since we have to do this work before we call __super__.fetch()
        if (this.global_search_options) {
            this.global_search_options.update_paginator(this.paginator); // add group_id to our paginator
        }

        var data = $.extend(options.data, {
            paginator: JSON.stringify(this.paginator),
            search_criteria: JSON.stringify(this.search_criterias),
            dashboard_filters: JSON.stringify(this.dashboard_filters)
        });
        var hash_str_input = JSON.stringify(data, Object.keys(data).sort());
        return `mobile_dashboard__${$f.utils.hash_str(hash_str_input)}`;
    },

    /**
     * Override this method to not save caches if the response has a zenput_code in it.
     */
    _persist_to_cache: function (callback) {
        // @jira:MOBILE-622 we do not want to ever cache zenput codes other than NO_DATA_IN_RANGE (i.e., error zenput_codes)
        if (this.zenput_code && this.zenput_code !== ZenputCodes.NO_DATA_IN_RANGE) {
            return callback();
        }

        MobileBaseDashboardCardResultList.__super__._persist_to_cache.call(this, callback);
    },

    // add zenput_code to cache on the collection
    to_cacheable_json: function () {
        return {
            results: this.toJSON(),
            zenput_code: this.zenput_code
        };
    },

    parse_cached_json: function (data) {
        if (data.zenput_code) {
            this.zenput_code = data.zenput_code;
        }
        return data.results;
    }
});

/* eslint-disable-next-line max-len */
export const DashboardCardResultListForTasksCurrent = MobileBaseDashboardCardResultList.extend({
    initialize: function (models, options) {
        DashboardCardResultListForTasksCurrent.__super__.initialize.apply(this, arguments);

        // use new flag for uses_local, allows for backwards compatibility in the app while this rolls out.
        // @jira:MOBILE-869. see TasksCurrentMySQLQuerySource.get_search_results.compare_with_local_dates
        this.search_criterias.metadata.set('uses_local', true);

        // @jira:MOBILE-622 if this is for 'my tasks', let's speed the cache up so the results are more real-time
        if (this.search_criterias.findWhere({ field_id: 'assignee_user_id', value: $f.active_user.id })) {
            this.cache_ttl = 1000; // we don't want to cache the 'my tasks' as long as the other task chiclets
        }
    }
});

/* eslint-disable-next-line max-len */
export const DashboardCardResultListWithHeaders = MobileBaseDashboardCardResultList.extend(
    _.extend(
        {
            mixin_super: function () {
                return DashboardCardResultListWithHeaders.__super__;
            },

            /**
             * On mobile, we need to serialize our headers when we save to local storage.
             */
            to_cacheable_json: function () {
                return {
                    headers: this.headers.toJSON(),
                    results: this.toJSON()
                };
            },

            parse_cached_json: function (data) {
                if (data.headers) {
                    this.set_headers(data.headers);
                }

                return data.results;
            }
        },
        DashboardCardResultListWithHeadersMixin
    )
);

export const MobileRecurringProjects = DashboardCardResultListWithHeaders.extend({
    comparator: function (first, second) {
        // "All projects" row should always be first
        if (first.get('info').is_all_projects) {
            return -1; // first is first
        } else if (second.get('info').is_all_projects) {
            return 1; // second is first
        }
    }
});

/**
 * A headers collection that knows how to sort itself. Necessary for the logbook because our UI shows older dates first.
 */
export const LogbookTopItemsHeadersList = TopItemHeaders.extend({
    /**
     * Use epoch time number to determine sort order
     *
     * @author:david we may someday want to bring this over to web too, but I don't think it's necessary
     * until we start paginating via the server there.
     */
    comparator: function (item) {
        return moment(item.get('sort_key')).toDate().getTime();
    }
});

export const LogbookResults = DashboardCardResultListWithHeaders.extend({
    // we dont use the normal paginators for this,
    // but if we dont set to 9999, the super will truncate the response coming back
    fetch: function (options) {
        this.paginator.set('limit', 9999);
        this.paginator.set('order_by', 'role-date');
        return LogbookResults.__super__.fetch.apply(this, arguments);
    },

    /**
     * Set headers as normal but using our own LogbookTopItemsHeadersList.
     */
    set_headers: function (headers) {
        this.headers = this.headers || new LogbookTopItemsHeadersList();
        LogbookResults.__super__.set_headers.apply(this, arguments);
    },

    parse: function (response) {
        var this_ = this;
        var args_ = arguments;

        var _super = function () {
            return LogbookResults.__super__.parse.apply(this_, args_);
        };

        if (!response || !response.headers) {
            return _super();
        }

        this.set_headers(response.headers);

        if (!this.length) {
            return _super(); // we are starting off fresh with new rows, so nothing fancy to do
        }

        // we already have rows, so JUST update their columns - we can assume there won't be any new rows
        this.each(function (row) {
            var existing_columns = row.get('columns');
            var existing_column_header_ids = _.map(existing_columns, function (value) {
                return value.header_id;
            });

            var new_row = _.filter(response.results, function (result) {
                return result.info.project.id === row.get('info').project.id;
            })[0];

            if (!new_row) {
                // if the project wasn't returned in the response, add mocked data with { value: null }
                _.each(response.headers, function (value, idx) {
                    existing_columns.push({ header_id: value.id, info: {}, value: null });
                });
                return true;
            }

            // maintain pass-by-reference by adding new columns via loop instead of using Array.concat()
            _.each(new_row.columns, function (col) {
                // only add new data to the model, otherwise update the columns on re-fetch from cache ttl
                var existing_header_id_match_idx = existing_column_header_ids.indexOf(col.header_id);
                if (existing_header_id_match_idx == -1) {
                    existing_columns.push(col);
                } else {
                    existing_columns[existing_header_id_match_idx] = col;
                }
            });
        });

        return this.models; // don't want to call super in this case, as that would overwrite all our existing rows
    }
});

/**
 * We are using a new set of search options here for projects filtered by tasks.
 * This avoids problems with having to reset project_id on task views depending on
 * which path your reach the task list from.
 */
export const LogBookTaskSearchOptions = ProjectTaskSearchOptions.extend({
    cache_key: 'logbook_task_search_options',

    initialize_options: function () {
        LogBookTaskSearchOptions.__super__.initialize_options.apply(this, arguments);

        this.add([
            new SearchOption({
                field: 'include_future_tasks_v2',
                value: true
            }),
            new SearchOption({
                field: 'status',
                value: 'all'
            })
        ]);

        // Remove the 'filter' search option for LogBookTaskSearchOptions
        this.remove(this.findWhere({ field: 'filter' }));
    },

    // no user filtering allowed, so don't display this
    get_filter_string: function () {
        return null;
    }
});

export const ImageMetadata = BaseZenputModel.extend({
    defaults: {
        form_field: null, // {id: null, title: ""}
        form_submission: null,
        account: null
    }
});

export const GalleryImage = BaseDashboardCardResultListItem.extend({
    defaults: {
        url: null,
        metadata: null // ImageMetadata
    },

    parse: function (response) {
        var account = $f.mobile.dashboard.get_active_account_or_group();
        return {
            url: response.url,
            metadata: new ImageMetadata({
                form_field: response.form_field,
                form_submission: response.form_submission,
                account: { id: account.id, title: account.get('name') }
            })
        };
    },

    get_full_url: function (use_native_url) {
        use_native_url = use_native_url || false;

        if (!this.get('url')) {
            return null;
        }

        if ($f.storage.is_data_uri(this.get('url'))) {
            if (isWindows()) {
                // for whatever reason, cdvfile links don't always work on windows. Use ms-appdata instead.
                return this.get('url').replace('cdvfile://localhost/persistent/', 'ms-appdata:///local/');
            }

            if (use_native_url) {
                if (!this.get('native_url')) {
                    return null;
                }
                return this.get('native_url');
            }

            return this.get('url');
        }

        return $f.storage.get_storage_full_url(this.get('url'));
    },
    is_image_data_uri: function () {
        return this.get('url').indexOf('data:image') === 0;
    }
});

export const GalleryImages = MobileBaseDashboardCardResultList.extend({
    model: GalleryImage,
    account: null, // Account
    paginator: null, // Paginator
    cache_enabled: false, // need becaue server count is sent back incorrectlly.

    initialize: function (models, options) {
        this.account = options.account;
        this.search_criterias = new SearchCriterias();
        this.search_criterias.metadata.set('type', 'location_gallery'); // query_key
        this.search_criterias.metadata.set('company', { id: $f.active_company.id });

        this.search_criterias.add(
            new SearchCriteria({
                field_id: 'account_id',
                operator_id: '=',
                value: this.account.id,
                display_value: this.account.get('name')
            })
        );

        this.dashboard_filters = new DashboardFilterList();

        GalleryImages.__super__.initialize.apply(this, arguments);
        // elasticsearch returns images grouped by submission, so the limit here is 40 submissions
        this.paginator = new Paginator({
            start: 0,
            limit: 40
        });
    },

    to_endcoded_URI: function () {
        // @todo @author:halah @jira:mobile-739 too many variables used once, refactor?
        var limit = encodeURI(JSON.stringify(this.paginator.get('limit')));
        var search_criteria = encodeURI(JSON.stringify(this.search_criterias));
        var dashboard_filters = encodeURI(JSON.stringify(this.dashboard_filters));

        return `search_criteria=${search_criteria}&dashboard_filters=${dashboard_filters}&limit=${limit}&order_by=name&tz=${$f.active_user.get(
            'time_zone'
        )}&search_by=account&select_all=false`;
    }
});
